import dayjs from "dayjs";

export const DEFAULT_POPUP_CONFIG = {
  close: {
    isClosable: true,
    closeDelaySeconds: 0,
    autoCloseSeconds: 0,
    closeButtonPosition: "top-right",
  },
  trigger: {
    triggerType: "on_load",
    triggerDelaySeconds: 0,
  },
  frequency: {
    displayFrequency: "always",
    frequencyCapCount: 0,
    frequencyCapPeriodHours: 0,
    frequencyMinGap: 0,
  },
};

export const DISPLAY_MODES = [
  { name: "Inline", value: "inline" },
  { name: "Popup", value: "popup" },
];

export const BANNER_SIZES = [
  { name: "Standard", value: "standard" },
  { name: "Compact", value: "compact" },
];

export const SCREEN_POSITIONS = [
  { name: "1 Earn Screen Block 1", value: 1 },
  { name: "2 Earn Screen Block 2", value: 2 },
  { name: "3 Games Page", value: 3 },
  { name: "4 Offer Page", value: 4 },
  { name: "5 Survey Page", value: 5 },
  { name: "6 Arena Page", value: 6 },
  { name: "7 Store Page", value: 7 },
  { name: "8 Transaction Screen", value: 8 },
  { name: "9 Exclusive Page", value: 9 },
  { name: "10 Giftcard Page", value: 10 },
  { name: "11 Analysis Page", value: 11 },
];

export const SCREEN_POSITION_MAP = {
  1: "Earn Screen Block 1",
  2: "Earn Screen Block 2",
  3: "Games Page",
  4: "Offer Page",
  5: "Survey Page",
  6: "Arena Page",
  7: "Store Page",
  8: "Transaction Screen",
  9: "Exclusive Page",
  10: "Giftcard Page",
  11: "Analysis Page",
};

// Number of compact ("ads" style) banner positions available per screen.
// Screens not listed here default to a single slot.
export const AD_SLOT_BY_SCREEN = {
  3: [{ name: "Position 1", value: 1 }],
  4: [
    { name: "Top", value: 1 },
    { name: "Bottom", value: 2 },
  ],
  5: [
    { name: "Position 1", value: 1 },
    { name: "Position 2", value: 2 },
  ],
  8: [{ name: "Position 1", value: 1 }],
  9: [
    { name: "Position 1", value: 1 },
    { name: "Position 2", value: 2 },
  ],
  10: [{ name: "Position 1", value: 1 }],
  11: [{ name: "Position 1", value: 1 }],
};

export const getAdSlotOptions = (screenPosition) =>
  AD_SLOT_BY_SCREEN[screenPosition] || [{ name: "Position 1", value: 1 }];

export const shouldShowAdSlotDropdown = (screenPosition) =>
  getAdSlotOptions(screenPosition).length > 1;

export const clampAdSlot = (screenPosition, adSlot) => {
  const options = getAdSlotOptions(screenPosition);
  const validValues = options.map((option) => option.value);
  if (validValues.includes(adSlot)) {
    return adSlot;
  }
  return options[0].value;
};

export const showCompactAdSlotField = (formData) =>
  formData.displayMode === "inline" && formData.bannerSize === "compact";

export const REDIRECT_TYPES = [
  { name: "Partner", value: "partner" },
  { name: "Internal", value: "internal" },
  { name: "Offer", value: "offer" },
  { name: "Custom", value: "custom" },
  { name: "Video", value: "video" },
  { name: "None", value: "none" },
];

export const USER_SEGMENTS = [
  { name: "All Users", value: "all" },
  { name: "New Users", value: "new" },
  { name: "Active Users", value: "active" },
  { name: "Churned Users", value: "churned" },
  { name: "Premium Users", value: "premium" },
  { name: "Locked Wallet Users", value: "lockedWallet" },
];

export const INTERNAL_ROUTES = [
  { name: "Brain Valley", value: "brainvalley" },
  { name: "Qmapi Surveys", value: "QmapiSurveys" },
];

export const CLOSE_BUTTON_POSITIONS = [
  { name: "Top Right", value: "top-right" },
  { name: "Top Left", value: "top-left" },
  { name: "Bottom Center", value: "bottom-center" },
];

export const TRIGGER_TYPES = [
  { name: "On Load", value: "on_load" },
  { name: "Delayed", value: "delayed" },
  { name: "On Action", value: "on_action" },
];

export const PACKAGE_INCLUDE_MATCH_OPTIONS = [
  { name: "Any", value: "any" },
  { name: "All", value: "all" },
];

export const DISPLAY_FREQUENCIES = [
  { name: "Always", value: "always" },
  { name: "Once Per Session", value: "once_per_session" },
  { name: "Once Per Day", value: "once_per_day" },
  { name: "Once Per User", value: "once_per_user" },
  { name: "Custom", value: "custom" },
];

export const FILTER_ALL_OPTION = { name: "All", value: "" };

export const FULL_BANNER_FILTER_OPTIONS = [
  FILTER_ALL_OPTION,
  { name: "Yes", value: 1 },
  { name: "No", value: 2 },
];

export const DISPLAY_MODE_FILTER_OPTIONS = [
  FILTER_ALL_OPTION,
  { name: "Inline", value: "inline" },
  { name: "Popup", value: "popup" },
];

export const BANNER_SIZE_FILTER_OPTIONS = [
  FILTER_ALL_OPTION,
  { name: "Standard", value: "standard" },
  { name: "Compact", value: "compact" },
];

export const isPopupBanner = (banner) => banner?.displayMode === "popup";

export const getAvailableScreenPositions = (displayMode) =>
  displayMode === "popup"
    ? SCREEN_POSITIONS.filter((pos) => pos.value !== 2)
    : SCREEN_POSITIONS;

export const showInlineDesignFields = (formData) =>
  formData.displayMode === "inline" && !formData.fullBanner;

// Title/subtitle are only mandatory for standard-size inline banners. Compact
// ("ads" style) banners ignore them entirely, matching backend validation.
export const isTitleSubtitleRequired = (formData) =>
  formData.displayMode === "inline" &&
  formData.bannerSize !== "compact" &&
  !formData.fullBanner;

export const buildBannerSubmitPayload = (formData, { countries, startDate, endDate }) => {
  const isPopup = formData.displayMode === "popup";

  const payload = {
    displayMode: formData.displayMode,
    imageUrl: formData.imageUrl,
    screenPosition: formData.screenPosition,
    redirectType: formData.redirectType,
    customId: formData.customId || "",
    internalRoute: formData.internalRoute || "",
    customUrl: formData.customUrl || "",
    isActive: formData.isActive,
    countries,
    userSegment: formData.userSegment,
    isScheduled: formData.isScheduled,
    start_date: startDate,
    end_date: endDate,
    priority: Number(formData.priority) || 0,
    name: formData.name || "",
  };

  if (isPopup) {
    payload.popupConfig = formData.popupConfig;
    return payload;
  }

  payload.bannerSize = formData.bannerSize || "standard";
  payload.fullBanner = formData.fullBanner;

  if (formData.bannerSize === "compact") {
    payload.adSlot = clampAdSlot(formData.screenPosition, formData.adSlot ?? 1);
  }

  if (!formData.fullBanner) {
    payload.title = formData.title;
    payload.subtitle = formData.subtitle;
    payload.backgroundColor = formData.backgroundColor;
    payload.buttonText = formData.buttonText;
    payload.buttonColor = formData.buttonColor;
  }

  return payload;
};

export const DEFAULT_TARGETING = {
  enabled: false,
  registeredBetween: { from: null, to: null },
  lastLoginBetween: { from: null, to: null },
  platformEarnings: { min: "", max: "" },
  partnerEarnings: [],
  withdrawalCount: { min: "", max: "" },
  withdrawnAmount: { min: "", max: "" },
  packages: { include: [], exclude: [], include_match: "any" },
  offersCompleted: { include: [], exclude: [], include_match: "any" },
  offersAttempted: { include: [], exclude: [], include_match: "any" },
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// dayjs' .toISOString() converts to the browser's local offset (e.g. IST,
// UTC+5:30), which shifts the calendar day backwards for anything before
// ~5:30am local time — midnight IST on the 1st becomes 18:30 UTC on the
// previous day. Since the "From"/"To" values are already normalized to
// local start-of-day/end-of-day, we just format the wall-clock date/time
// as-is and label it "Z" so the date the admin picked is exactly what gets
// sent, instead of silently shifting by the timezone offset.
const toIsoOrNull = (value) => (value ? `${value.format("YYYY-MM-DDTHH:mm:ss.SSS")}Z` : null);

// Every rule is optional. A range is only sent if at least one of its bounds
// was filled in; otherwise it collapses to null so the backend sees "no rule".
const buildRangeOrNull = (range, toValue) => {
  const from = toValue(range?.from);
  const to = toValue(range?.to);
  if (from === null && to === null) return null;
  return { from, to };
};

const buildMinMaxOrNull = (range, toValue) => {
  const min = toValue(range?.min);
  const max = toValue(range?.max);
  if (min === null && max === null) return null;
  return { min, max };
};

// Converts the UI targeting state into the API shape. If nothing was
// filled in anywhere (and the rule set isn't explicitly enabled), returns
// null so the whole "targeting" key can be omitted/nulled in the payload.
export const buildTargetingPayload = (targeting) => {
  if (!targeting) return null;

  const registeredBetween = buildRangeOrNull(targeting.registeredBetween, toIsoOrNull);
  const lastLoginBetween = buildRangeOrNull(targeting.lastLoginBetween, toIsoOrNull);
  const platformEarnings = buildMinMaxOrNull(targeting.platformEarnings, toNumberOrNull);
  const withdrawalCount = buildMinMaxOrNull(targeting.withdrawalCount, toNumberOrNull);
  const withdrawnAmount = buildMinMaxOrNull(targeting.withdrawnAmount, toNumberOrNull);

  const partnerEarningsList = (targeting.partnerEarnings || [])
    .filter((entry) => entry?.partnerId)
    .map((entry) => ({
      partnerId: entry.partnerId,
      min: toNumberOrNull(entry.min),
      max: toNumberOrNull(entry.max),
    }));
  const partnerEarnings = partnerEarningsList.length ? partnerEarningsList : null;

  const includePackages = targeting.packages?.include || [];
  const excludePackages = targeting.packages?.exclude || [];
  const packages =
    includePackages.length || excludePackages.length
      ? {
          include: includePackages,
          exclude: excludePackages,
          include_match: targeting.packages?.include_match || "any",
        }
      : null;

  const buildIncludeExcludeOrNull = (rule) => {
    const include = rule?.include || [];
    const exclude = rule?.exclude || [];
    if (!include.length && !exclude.length) return null;
    return {
      include,
      exclude,
      include_match: rule?.include_match || "any",
    };
  };

  const offersCompleted = buildIncludeExcludeOrNull(targeting.offersCompleted);
  const offersAttempted = buildIncludeExcludeOrNull(targeting.offersAttempted);

  const hasAnyRule =
    !!registeredBetween ||
    !!lastLoginBetween ||
    !!platformEarnings ||
    !!withdrawalCount ||
    !!withdrawnAmount ||
    !!partnerEarnings ||
    !!packages ||
    !!offersCompleted ||
    !!offersAttempted;

  if (!targeting.enabled && !hasAnyRule) {
    return null;
  }

  return {
    enabled: !!targeting.enabled,
    registeredBetween,
    lastLoginBetween,
    platformEarnings,
    partnerEarnings,
    withdrawalCount,
    withdrawnAmount,
    packages,
    offersCompleted,
    offersAttempted,
  };
};

// Reverses toIsoOrNull: the stored string's date/time was the admin's
// local wall-clock value with a "Z" appended (not a real UTC instant), so
// strip the "Z" and parse it as local time again to avoid shifting the day.
const toDayjsOrNull = (value) => (value ? dayjs(String(value).replace(/Z$/, "")) : null);

// Offer include/exclude entries may come back from the API either as plain
// ObjectId strings or as populated offer documents (with name/partner info).
// This pulls out the ids for the UI state, plus a lookup map of any labels
// found so chips can show a readable name instead of a raw id.
const extractOfferEntries = (list) => {
  const ids = [];
  const labels = {};

  (list || []).forEach((entry) => {
    if (!entry) return;

    if (typeof entry === "object") {
      const id = entry._id ? String(entry._id) : null;
      if (!id) return;
      ids.push(id);
      labels[id] = {
        _id: id,
        name: entry.name || id,
        p_offer_id: entry.p_offer_id || "",
        partnerName: entry.partnerName || entry.provider?.name || "",
      };
      return;
    }

    ids.push(String(entry));
  });

  return { ids, labels };
};

// Converts the API targeting shape back into the UI state shape used by
// the Create/Edit banner forms (the inverse of buildTargetingPayload).
export const normalizeTargetingFromApi = (targeting) => {
  if (!targeting) {
    return { targeting: { ...DEFAULT_TARGETING }, offerOptionLabels: {} };
  }

  const includeCompleted = extractOfferEntries(targeting.offersCompleted?.include);
  const excludeCompleted = extractOfferEntries(targeting.offersCompleted?.exclude);
  const includeAttempted = extractOfferEntries(targeting.offersAttempted?.include);
  const excludeAttempted = extractOfferEntries(targeting.offersAttempted?.exclude);

  const offerOptionLabels = {
    ...includeCompleted.labels,
    ...excludeCompleted.labels,
    ...includeAttempted.labels,
    ...excludeAttempted.labels,
  };

  return {
    targeting: {
      enabled: !!targeting.enabled,
      registeredBetween: {
        from: toDayjsOrNull(targeting.registeredBetween?.from),
        to: toDayjsOrNull(targeting.registeredBetween?.to),
      },
      lastLoginBetween: {
        from: toDayjsOrNull(targeting.lastLoginBetween?.from),
        to: toDayjsOrNull(targeting.lastLoginBetween?.to),
      },
      platformEarnings: {
        min: targeting.platformEarnings?.min ?? "",
        max: targeting.platformEarnings?.max ?? "",
      },
      partnerEarnings: (targeting.partnerEarnings || []).map((entry) => ({
        partnerId: entry?.partnerId || "",
        min: entry?.min ?? "",
        max: entry?.max ?? "",
      })),
      withdrawalCount: {
        min: targeting.withdrawalCount?.min ?? "",
        max: targeting.withdrawalCount?.max ?? "",
      },
      withdrawnAmount: {
        min: targeting.withdrawnAmount?.min ?? "",
        max: targeting.withdrawnAmount?.max ?? "",
      },
      packages: {
        include: targeting.packages?.include || [],
        exclude: targeting.packages?.exclude || [],
        include_match: targeting.packages?.include_match || "any",
      },
      offersCompleted: {
        include: includeCompleted.ids,
        exclude: excludeCompleted.ids,
        include_match: targeting.offersCompleted?.include_match || "any",
      },
      offersAttempted: {
        include: includeAttempted.ids,
        exclude: excludeAttempted.ids,
        include_match: targeting.offersAttempted?.include_match || "any",
      },
    },
    offerOptionLabels,
  };
};

export const buildBannerListRequestBody = (filters, searchValue = "") => {
  const body = {
    page: filters.page ?? 0,
    limit: filters.limit ?? 10,
    isScheduled: filters.isScheduled ?? 3,
    redirectType: filters.redirectType || "",
    screenPosition: filters.screenPosition || "",
    fullBanner: filters.fullBanner ?? 3,
    displayMode: filters.displayMode || "",
    bannerSize: filters.bannerSize || "",
    searchValue: searchValue || "",
    s_date: filters.s_date ?? null,
    e_date: filters.e_date ?? null,
  };

  // Omit isActive → backend returns active-only. 1=active, 2=inactive, 3=all.
  if (filters.isActive === 1 || filters.isActive === 2 || filters.isActive === 3) {
    body.isActive = filters.isActive;
  }

  return body;
};

export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;
